import i18n from 'i18next';
import drawingTool from 'pages/easy-estimator/components/scrollable-area/design-countertop/drawer/drawingTool';
import { makeSinksSelector } from 'pages/easy-estimator/components/scrollable-area/sinks/sinks.utils';
import { useCallback, useContext, useMemo } from 'react';

import { CANVAS_OBJECT_TYPE_PREFIX, DEFAULT_SIDE_ITEMS } from '@common/constants';
import { ShapeType } from '@common/enums';
import { selectBestMatchingColor } from '@common/hooks/useEquivalentProductMatching/utils/selectBestMatchingColor';
import {
    ApplianceModel,
    ApplianceSource,
    ApplianceType,
    ChipMinimizerModel,
    CornerRadiusModel,
    CutoutModel,
    DrawingAppliance,
    DrawingShape,
    EdgeModel,
    OptionalProduct,
    PlumbingProduct,
    SideItemInfoModel,
    TearOutModel,
} from '@common/models';
import { getSideItemsFromShape } from '@common/utils/sideItems.utils';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useLazyFetchCornerRadiiQuery } from '@ee-api/corner-radii-api/cornerRadiiService';
import { useCornerRadii } from '@ee-api/corner-radii-api/hooks/useCornerRadii';
import { useLazyFetchDiagonalCornersQuery } from '@ee-api/diagonal-corners-api/diagonalCornersService';
import { useDiagonalCorners } from '@ee-api/diagonal-corners-api/useDiagonalCorners';
import { useLazyFetchEdgesQuery } from '@ee-api/edges-api/edgesService';
import { selectFilteredEdges } from '@ee-api/edges-api/useEdges';
import { useLazyFetchMaterialsQuery } from '@ee-api/materials-api/materialsService';
import { parseMaterialsApiResults } from '@ee-api/materials-api/useMaterials';
import { selectDefaultMaterial, useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useLazyFetchOptionalItemsQuery } from '@ee-api/optional-items-api/optionalItemsService';
import { OptionalItemKeys, useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { useLazyFetchPlumbingQuery } from '@ee-api/plumbing-api/plumbingService';
import { usePlumbing } from '@ee-api/plumbing-api/usePlumbing';
import { CommonProductQueryParams } from '@ee-api/queryParams';
import { useLazyFetchCustomerSuppliedSinksQuery, useLazyFetchProgramSinksQuery } from '@ee-api/sinks-api/sinksService';
import { useLazyFetchStoresQuery } from '@ee-api/stores-api/storesService';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext, OptionalProductContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectSelectedChipMinimizers, updateSelectedChipMinimizers } from '@redux/slices/chipMinimizerSlice';
import { selectRestorableColor } from '@redux/slices/colorSlice';
import { selectSelectedCutouts, updateSelectedCutouts } from '@redux/slices/cutoutSlice';
import { resetActiveDrawingAppliance, selectActiveDrawingAppliance } from '@redux/slices/drawingShapeSlice';
import { selectRestorableMaterial } from '@redux/slices/materialSlice';
import { selectSelectedPlumbing, updateSelectedPlumbing } from '@redux/slices/plumbingSlice';
import { selectRestorableStoreId } from '@redux/slices/storeSlice';
import { selectSelectedTearOuts, updateSelectedTearOuts } from '@redux/slices/tearOutSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import { useLazyFetchCustomerSuppliedCookingAppliancesQuery } from '../../../api/static-content-api/cooking-appliance-api/cookingApplianceService';
import {
    AlertWithMetadata,
    DesignProductMatchingSummary,
    DrawingShapeItem,
    EstimateItem,
    ProductMatchingReturnParameters,
    ProductMatchingSummary,
    ProductMatchingSummaryBySelectedItems,
    ProductMatchingTargetParameters,
    SelectedItemWithFullRecord,
} from './interfaces/ProductMatchingSummary';
import {
    addSelectedItemWithFullRecordToSelectedItemMap,
    createDrawingShapesToSummaryReducer,
    createDrawingShapesToSummaryReducerUsingApplianceChecker,
    createDrawingShapesToSummaryReducerUsingCornersChecker,
    createSelectedItemToSelectedItemWithFullRecordMapper,
    createSelectedItemToSummaryReducer,
    isSelectedItemWithPropertiesAndIsSelected,
    summaryToAlertsWithMetadataReducer,
} from './utils/useEquivalentProductMatching.utils';

const emptyArray = [] satisfies DrawingShape[] | SelectedItemWithFullRecord[];

const INITIAL_PRODUCT_MATCHING_SUMMARY = {
    newValueAfterReplacements: emptyArray,
    kept: emptyArray,
    replacedWithEquivalent: emptyArray,
    removed: emptyArray,
} as const;

const emptyObject: Record<string, ProductMatchingSummary<EstimateItem, unknown>> = {};

export const matchingCheckSkipToken = 'MATCHING_CHECK_SKIP_TOKEN' as const;
export type MatchingCheckSkipToken = typeof matchingCheckSkipToken;

type DrawingShapesSummary = ProductMatchingSummary<DrawingShapeItem, DrawingShape>;

type ResetEdge = { shapeName: string; edgeName: string; sideItem: SideItemInfoModel };

export const useEquivalentProductMatching = () => {
    const dispatch = useAppDispatch();
    const activeDrawingAppliance = useAppSelector(selectActiveDrawingAppliance);

    const { drawingShapes, updateDrawingShapes } = useContext(DrawingShapeContext);
    const optionalProductContext = useContext(OptionalProductContext);

    /**Current catalogs to get full records when only saved by id */
    const { cutouts, tearOuts, chipMinimizer, optionalProducts } = useOptionalItems();
    const { plumbingItems } = usePlumbing();

    const selectedPlumbing = useAppSelector(selectSelectedPlumbing);
    const selectedCutouts = useAppSelector(selectSelectedCutouts);
    const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
    const selectedChipMinimizers = useAppSelector(selectSelectedChipMinimizers);
    const { cornerRadii } = useCornerRadii();
    const { diagonalCorners } = useDiagonalCorners();

    /** RTK lazy fetch method for new catalog */
    // const [fetchFaucets] = useLazyFetchInStoreFaucetsQuery();
    const [fetchEdges] = useLazyFetchEdgesQuery();
    const [fetchCornerRadii] = useLazyFetchCornerRadiiQuery();
    const [fetchDiagonalCorners] = useLazyFetchDiagonalCornersQuery();
    const [fetchOptionalItemsQuery] = useLazyFetchOptionalItemsQuery();
    const [fetchStores] = useLazyFetchStoresQuery();
    const [fetchMaterials] = useLazyFetchMaterialsQuery();
    const [fetchSinks] = useLazyFetchProgramSinksQuery();
    const [fetchCustomerSuppliedCookingAppliances] = useLazyFetchCustomerSuppliedCookingAppliancesQuery();
    const [fetchCustomerSuppliedSinksQuery] = useLazyFetchCustomerSuppliedSinksQuery();
    const [fetchPlumbing] = useLazyFetchPlumbingQuery();

    const currentZipCode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedColor } = useSelectedColor();
    const restorableStoreId = useAppSelector(selectRestorableStoreId);
    const restorableMaterial = useAppSelector(selectRestorableMaterial);
    const restorableColor = useAppSelector(selectRestorableColor);

    const selectSinksTransformed = useMemo(() => makeSinksSelector(), []);
    const selectSinksCustomerSuppliedTransformed = useMemo(() => makeSinksSelector(), []);

    /**
     * Given one or more target parameters for zipCode, Store, Material, Color, fetch and select the appropriate targes
     * Any parameters not provided default to the appropriate current selection
     * Any parameters (specified target or current) that are no longer valid (i.e. store not in new zip code, material not available in new store)
     *  results in an appopriate default selection
     * @returns {undefined} if failing to fetch stores or materials. Otherwise, returns CommonProductQueryParams with validated values
     */
    const getValidatedQueryParams = useCallback(
        async (args: ProductMatchingTargetParameters): Promise<ProductMatchingReturnParameters | undefined> => {
            // use any of the given values OR the currently selected values
            const {
                zipCode = currentZipCode,
                storeId = selectedStore?.companyId,
                material = { description: selectedMaterial?.description },
                color = {
                    productId: selectedColor?.productId,
                    masterSku: selectedColor?.masterSku,
                    thickness: selectedColor?.thickness,
                    name: selectedColor?.name,
                    brand: selectedColor?.brand,
                },
            } = args;

            // fetch stores for the new zip. For the old it won’t fetch from the API because of RTK query cache
            const rawStores = await fetchStores({ params: { zipCode } }, true);
            if (rawStores.isError || !rawStores?.data?.length) {
                console.warn(`Error fetching stores for zip code ${zipCode}`);
                return;
            }

            // and ensure the target store is in there, or select the default store for the new zip code
            const newStore =
                rawStores.data.find((store) => storeId && store.companyId === storeId) ??
                rawStores.data.find((store) => restorableStoreId && store.companyId === restorableStoreId) ??
                rawStores.data[0];

            // fetch materials based on the validated zip and store
            const rawMaterials = await fetchMaterials({ params: { zipCode, storeId: newStore.companyId } }, true);
            if (rawMaterials.isError || !rawMaterials?.data?.length) {
                console.warn(`Error fetching materials for zip code ${zipCode} and store ${newStore.companyId}`);
                return;
            }
            const materials = parseMaterialsApiResults(rawMaterials.data); //this is primarily so we're working with MaterialModel records

            // and try to find the target material (if changing materials), or the currently userSelectedMaterial currently exists
            // or select default material if not
            const newMaterial =
                materials?.find((testMaterial) => material && testMaterial.description === material.description) ??
                materials?.find(
                    (testMaterial) => restorableMaterial && testMaterial.description === restorableMaterial.description
                ) ??
                selectDefaultMaterial(materials, { description: selectedMaterial?.description });

            // colors comes from whatever material we just found
            const colors = newMaterial?.colors ?? emptyArray;

            // find the best match for the given color, or the selecteColor
            const newColor = selectBestMatchingColor(color, colors, restorableColor);

            if (!newColor) {
                console.warn(
                    `Error fetching colors for zip code ${zipCode}, store ${newStore?.companyId} and material ${newMaterial?.description}`
                );
                return;
            }

            // now return all the _validated_ values - all subsequent api lookups should be from these
            return {
                zipCode,
                storeId: newStore.companyId,
                store: newStore,
                prodQteGrpID: newColor.qteGrpID,
                material: newMaterial,
                color: newColor,
            };
        },
        [
            currentZipCode,
            fetchMaterials,
            fetchStores,
            restorableColor,
            restorableMaterial,
            restorableStoreId,
            selectedColor?.productId,
            selectedColor?.masterSku,
            selectedColor?.thickness,
            selectedColor?.name,
            selectedColor?.brand,
            selectedMaterial?.description,
            selectedStore?.companyId,
        ]
    );

    const fetchNewCatalogs = useCallback(
        async (
            commonProductQueryParams: CommonProductQueryParams
        ): Promise<{
            edgesNewCatalog: EdgeModel[] | undefined;
            cornersNewCatalog: CornerRadiusModel[] | undefined;
            appliancesNewCatalog: ApplianceModel[];
            chipMinimizersNewCatalog: ChipMinimizerModel[];
            cutoutsNewCatalog: CutoutModel[];
            tearOutsNewCatalog: TearOutModel[];
            otherOptionalItemsNewCatalog: OptionalProduct[];
            plumbingNewCatalog: PlumbingProduct[] | undefined;
        }> => {
            /** Get new catalog with lazy fetching The number are an aid to see if the destructuring name correspond to the fetch function*/
            const [
                { data: edgesNewCatalog }, //1
                { data: optionalItemsNewCatalog }, //2
                { data: _cornersNewCatalog }, //3
                { data: diagonalCorners }, //4
                { data: _sinksNewCatalog }, //5
                { data: _customerSuppliedSinksNewCatalog }, //6
                { data: cookingAppliancesNewCatalog }, //7
                { data: plumbingNewCatalog }, //8
            ] = await Promise.all([
                fetchEdges(commonProductQueryParams, true), //1
                fetchOptionalItemsQuery(commonProductQueryParams, true), //2
                fetchCornerRadii(commonProductQueryParams, true), //3
                fetchDiagonalCorners(commonProductQueryParams, true), //4
                fetchSinks(commonProductQueryParams, true), //5
                fetchCustomerSuppliedSinksQuery(commonProductQueryParams, true), //6
                fetchCustomerSuppliedCookingAppliances(commonProductQueryParams, true), //7
                fetchPlumbing(commonProductQueryParams, true), //8
            ]);

            const cornersNewCatalog = [...(_cornersNewCatalog ?? emptyArray), ...(diagonalCorners ?? emptyArray)];

            const customerSuppliedSinksNewCatalog = selectSinksCustomerSuppliedTransformed(
                _customerSuppliedSinksNewCatalog ?? emptyArray,
                ApplianceSource.PROGRAM,
                ApplianceType.SINK_LABOR
            );

            const sinksNewCatalog = selectSinksTransformed(
                _sinksNewCatalog ?? emptyArray,
                ApplianceSource.PROGRAM,
                ApplianceType.SINK,
                true
            );

            const appliancesNewCatalog: ApplianceModel[] = [
                ...(sinksNewCatalog ?? emptyArray),
                ...(customerSuppliedSinksNewCatalog ?? emptyArray),
                ...(cookingAppliancesNewCatalog ?? emptyArray),
            ];

            const _chipMinimizersNewCatalog = optionalItemsNewCatalog?.[OptionalItemKeys.chipMinimizer];

            const chipMinimizersNewCatalog: ChipMinimizerModel[] = _chipMinimizersNewCatalog
                ? [_chipMinimizersNewCatalog]
                : emptyArray;

            const cutoutsNewCatalog = optionalItemsNewCatalog?.cutouts ?? emptyArray;

            const tearOutsNewCatalog = optionalItemsNewCatalog?.[OptionalItemKeys.tearOuts] ?? emptyArray;

            const otherOptionalItemsNewCatalog = optionalItemsNewCatalog?.others ?? emptyArray;

            return {
                edgesNewCatalog,
                cornersNewCatalog,
                appliancesNewCatalog,
                chipMinimizersNewCatalog,
                cutoutsNewCatalog,
                tearOutsNewCatalog,
                otherOptionalItemsNewCatalog,
                plumbingNewCatalog,
            };
        },
        [
            fetchCornerRadii,
            fetchCustomerSuppliedCookingAppliances,
            fetchCustomerSuppliedSinksQuery,
            fetchDiagonalCorners,
            fetchEdges,
            fetchOptionalItemsQuery,
            fetchPlumbing,
            fetchSinks,
            selectSinksCustomerSuppliedTransformed,
            selectSinksTransformed,
        ]
    );

    const calculateSummaryFromDrawingShapes = useCallback(
        ({
            drawingShapes,
            newCatalogs: { edgesNewCatalog, appliancesNewCatalog, cornersNewCatalog },
        }: {
            drawingShapes: DrawingShape[];
            newCatalogs: {
                edgesNewCatalog: EdgeModel[] | undefined;
                appliancesNewCatalog: ApplianceModel[];
                cornersNewCatalog: CornerRadiusModel[] | undefined;
            };
        }): { summaryFromDrawingShapes: DrawingShapesSummary } => {
            const clonedDrawingShapes = drawingTool.cloneShapes(drawingShapes);
            const productMatchingSummaryFromEdges: ProductMatchingSummary<EdgeModel, DrawingShape> =
                drawingShapes.reduce(createDrawingShapesToSummaryReducer(edgesNewCatalog ?? emptyArray), {
                    ...INITIAL_PRODUCT_MATCHING_SUMMARY,
                    newValueAfterReplacements: clonedDrawingShapes,
                });

            const productMatchingSummaryFromAppliances =
                productMatchingSummaryFromEdges.newValueAfterReplacements.reduce(
                    createDrawingShapesToSummaryReducerUsingApplianceChecker([...appliancesNewCatalog]),
                    {
                        ...INITIAL_PRODUCT_MATCHING_SUMMARY,
                        newValueAfterReplacements: drawingTool.cloneShapes(
                            productMatchingSummaryFromEdges.newValueAfterReplacements
                        ), // Since checker mutate array that is reduced we need to clone initial array
                    }
                );

            const productMatchingSummaryAfterCheckingCorners =
                productMatchingSummaryFromAppliances.newValueAfterReplacements.reduce(
                    createDrawingShapesToSummaryReducerUsingCornersChecker(
                        cornersNewCatalog ?? emptyArray,
                        /** cornerRadii and diagonal corners merged into one array of current corners catalog */
                        [...cornerRadii, ...diagonalCorners]
                    ),
                    {
                        ...INITIAL_PRODUCT_MATCHING_SUMMARY,
                        newValueAfterReplacements: drawingTool.cloneShapes(
                            productMatchingSummaryFromAppliances.newValueAfterReplacements
                        ), // Since checker mutate array that is reduced we need to clone initial array
                    }
                );

            const summaryFromDrawingShapes: ProductMatchingSummary<DrawingShapeItem, DrawingShape> = {
                newValueAfterReplacements: productMatchingSummaryAfterCheckingCorners.newValueAfterReplacements,
                valueBeforeReplacements: drawingTool.cloneShapes(drawingShapes),
                kept: [
                    ...productMatchingSummaryFromEdges.kept,
                    ...productMatchingSummaryFromAppliances.kept,
                    ...productMatchingSummaryAfterCheckingCorners.kept,
                ],
                replacedWithEquivalent: [
                    ...productMatchingSummaryFromEdges.replacedWithEquivalent,
                    ...productMatchingSummaryFromAppliances.replacedWithEquivalent,
                    ...productMatchingSummaryAfterCheckingCorners.replacedWithEquivalent,
                ],
                removed: [
                    ...productMatchingSummaryFromEdges.removed,
                    ...productMatchingSummaryFromAppliances.removed,
                    ...productMatchingSummaryAfterCheckingCorners.removed,
                ],
            };

            return { summaryFromDrawingShapes };
        },
        [cornerRadii, diagonalCorners]
    );

    /**
     * Checks the current design (defined as the drawingShapes and selectedItems (cutouts, tear-outs,etc.))
     * against a new catalog that is built from the args (zipCode, storeId, qteGrpId)
     *
     * @returns {ProductMatchingSummary}
     * */
    const equivalentProductMatchingCheck = useCallback(
        async ({
            zipCode,
            storeId,
            prodQteGrpID: qteGrpID,
            material,
            color,
        }: ProductMatchingTargetParameters &
            Required<Required<CommonProductQueryParams>['params']>): Promise<DesignProductMatchingSummary> => {
            const commonProductQueryParams: CommonProductQueryParams = {
                params: { zipCode, storeId, prodQteGrpID: qteGrpID },
            };

            //Fetch new catalog for param candidates (zipCode, storeId and prodQteGrpID)
            const {
                edgesNewCatalog,
                cornersNewCatalog,
                tearOutsNewCatalog,
                chipMinimizersNewCatalog,
                appliancesNewCatalog,
                cutoutsNewCatalog,
                otherOptionalItemsNewCatalog,
                plumbingNewCatalog,
            } = await fetchNewCatalogs(commonProductQueryParams);

            const filteredEdges = selectFilteredEdges(edgesNewCatalog ?? emptyArray, material, color);

            // Summary from drawingShapes
            const { summaryFromDrawingShapes } = calculateSummaryFromDrawingShapes({
                drawingShapes,
                newCatalogs: {
                    edgesNewCatalog: filteredEdges,
                    appliancesNewCatalog,
                    cornersNewCatalog,
                },
            });

            const summaryFromOptionalProducts: ProductMatchingSummary<
                SelectedItemWithFullRecord,
                SelectedItemWithFullRecord
            > = Array.from(optionalProductContext?.selectedOptionalProducts?.entries?.() ?? emptyArray)
                .map<SelectedItemWithFullRecord | undefined>(
                    createSelectedItemToSelectedItemWithFullRecordMapper(optionalProducts)
                )
                .filter(isSelectedItemWithPropertiesAndIsSelected)
                .reduce<ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord>>(
                    createSelectedItemToSummaryReducer(otherOptionalItemsNewCatalog),
                    INITIAL_PRODUCT_MATCHING_SUMMARY
                );

            const summaryForPlumbingSelectedItems: ProductMatchingSummary<
                SelectedItemWithFullRecord,
                SelectedItemWithFullRecord
            > = Array.from(selectedPlumbing.entries())
                .map<SelectedItemWithFullRecord | undefined>(
                    createSelectedItemToSelectedItemWithFullRecordMapper(plumbingItems)
                )
                .filter(isSelectedItemWithPropertiesAndIsSelected)
                .reduce<ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord>>(
                    createSelectedItemToSummaryReducer(plumbingNewCatalog ?? []),
                    INITIAL_PRODUCT_MATCHING_SUMMARY
                );

            //Summary for selected items: cutouts, tear-outs, chip-minimizer
            const productMatchingSummariesFromSelectedItems: ProductMatchingSummaryBySelectedItems = {
                // reduce arg:
                cutouts: Array.from(selectedCutouts.entries())
                    .map<SelectedItemWithFullRecord | undefined>(
                        createSelectedItemToSelectedItemWithFullRecordMapper(cutouts)
                    )
                    .filter(isSelectedItemWithPropertiesAndIsSelected)
                    .reduce<ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord>>(
                        createSelectedItemToSummaryReducer(cutoutsNewCatalog),
                        INITIAL_PRODUCT_MATCHING_SUMMARY
                    ),
                [OptionalItemKeys.chipMinimizer]: Array.from(selectedChipMinimizers.entries())
                    .map<SelectedItemWithFullRecord | undefined>(
                        createSelectedItemToSelectedItemWithFullRecordMapper(chipMinimizer ? [chipMinimizer] : [])
                    )
                    .filter(isSelectedItemWithPropertiesAndIsSelected)
                    .reduce<ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord>>(
                        createSelectedItemToSummaryReducer(chipMinimizersNewCatalog),
                        INITIAL_PRODUCT_MATCHING_SUMMARY
                    ),
                [OptionalItemKeys.tearOuts]: Array.from(selectedTearOuts.entries())
                    .map<SelectedItemWithFullRecord | undefined>(
                        createSelectedItemToSelectedItemWithFullRecordMapper(tearOuts)
                    )
                    .filter(isSelectedItemWithPropertiesAndIsSelected)
                    .reduce<ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord>>(
                        createSelectedItemToSummaryReducer(tearOutsNewCatalog),
                        INITIAL_PRODUCT_MATCHING_SUMMARY
                    ),
            };

            //Design productMatchingSummary
            // console.log('== productMatchingSummary', {
            //     ...productMatchingSummariesFromSelectedItems,
            //     drawingShapes: summaryFromDrawingShapes,
            //     selectedOptionalProducts: summaryFromOptionalProducts,
            // });

            return {
                ...productMatchingSummariesFromSelectedItems,
                drawingShapes: summaryFromDrawingShapes,
                selectedOptionalProducts: summaryFromOptionalProducts,
                plumbingSummary: summaryForPlumbingSelectedItems,
            };
        },
        [
            fetchNewCatalogs,
            calculateSummaryFromDrawingShapes,
            drawingShapes,
            optionalProductContext?.selectedOptionalProducts,
            optionalProducts,
            selectedPlumbing,
            plumbingItems,
            selectedCutouts,
            cutouts,
            selectedChipMinimizers,
            chipMinimizer,
            selectedTearOuts,
            tearOuts,
        ]
    );

    /**
     * Runs an equivalentProductMatchingCheck against a new catalog defined by args
     * If some args are undefined. These are inferred using defaults
     * Does not update the design
     * @returns {DesignProductMatchingSummary} or null if failing to get commonProductQueryParams
     */
    const makeDesignProductMatchingSummary = useCallback(
        async (args: ProductMatchingTargetParameters): Promise<DesignProductMatchingSummary | null> => {
            const validatedQueryParams = await getValidatedQueryParams(args);

            if (!validatedQueryParams) {
                return null;
            }

            return await equivalentProductMatchingCheck(validatedQueryParams);
        },
        [equivalentProductMatchingCheck, getValidatedQueryParams]
    );

    const isAppliance = (item: DrawingShapeItem): item is DrawingAppliance =>
        !!(item as DrawingAppliance).applianceModel;

    /**
     * Updates design with the new values after an equivalentProductMatchingCheck (where each product is checked against the new catalog. The result is a summary that contains an array of the products kept, replacedWithEquivalent, the removed, and  the new design  )
     * If failing to get commonProductQueryParams, this function does nothing.
     * */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const replaceProductsWithEquivalents = async (args: ProductMatchingTargetParameters): Promise<void> => {
        const validParams = await getValidatedQueryParams(args);

        /**If failing to get validParams do nothing */
        if (!validParams) {
            return;
        }

        const productMatchingSummaries = await equivalentProductMatchingCheck(validParams);

        // optional products
        optionalProductContext?.resetSelectedOptionalProducts();
        optionalProductContext?.assignSelectedOptionalProducts(
            productMatchingSummaries.selectedOptionalProducts.newValueAfterReplacements.reduce(
                addSelectedItemWithFullRecordToSelectedItemMap,
                new Map()
            )
        );

        // updateSelectedPlumbing
        dispatch(
            updateSelectedPlumbing(
                productMatchingSummaries.plumbingSummary.newValueAfterReplacements.reduce(
                    addSelectedItemWithFullRecordToSelectedItemMap,
                    new Map()
                )
            )
        );

        // updateCutouts
        dispatch(
            updateSelectedCutouts(
                productMatchingSummaries.cutouts.newValueAfterReplacements.reduce(
                    addSelectedItemWithFullRecordToSelectedItemMap,
                    new Map()
                )
            )
        );

        // update tear-outs
        dispatch(
            updateSelectedTearOuts(
                productMatchingSummaries[OptionalItemKeys.tearOuts].newValueAfterReplacements.reduce(
                    addSelectedItemWithFullRecordToSelectedItemMap,
                    new Map()
                )
            )
        );

        //update chip-minimizer
        dispatch(
            updateSelectedChipMinimizers(
                productMatchingSummaries[OptionalItemKeys.chipMinimizer].newValueAfterReplacements.reduce(
                    addSelectedItemWithFullRecordToSelectedItemMap,
                    new Map()
                )
            )
        );

        // Reset active appliance if it was removed
        const activeApplianceInRemoved = [...productMatchingSummaries.drawingShapes.removed].find(
            (item) =>
                isAppliance(item) && `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${item.id}` === activeDrawingAppliance
        );

        if (activeApplianceInRemoved) {
            dispatch(resetActiveDrawingAppliance());
        }

        // Clear any invalid side items based on any materialFilter values
        const newMaterial = args.material?.description;
        const typesToRemove = DEFAULT_SIDE_ITEMS.filter(
            (item) => newMaterial && item.materialFilter && !newMaterial?.match(item.materialFilter)
        ).map((item) => item.sideType);
        const edgesToReset = drawingShapes
            .filter((shape) => shape.type !== ShapeType.FAUX_SHAPE)
            .reduce((acc, shape) => {
                const sideItems = getSideItemsFromShape(shape)
                    .filter((item) => typesToRemove.some((type) => type === item.sideType))
                    .map((sideItem) => {
                        return {
                            shapeName: shape.canvasObjectName,
                            edgeName: sideItem.arrowInfo?.label,
                            sideItem,
                        } as ResetEdge;
                    });
                return [...acc, ...sideItems];
            }, [] as ResetEdge[]);

        if (edgesToReset.length) {
            // update drawingshapes with all the resetEdges values
            productMatchingSummaries.drawingShapes.newValueAfterReplacements.forEach((shape) => {
                const resetsForThisShape = edgesToReset.filter((reset) => reset.shapeName === shape.canvasObjectName);
                if (!!shape.arrowsInfo && resetsForThisShape.length) {
                    shape.arrowsInfo.forEach((arrow) => {
                        const reset = resetsForThisShape.find((item) => item.edgeName === arrow.label);
                        if (reset) {
                            arrow.isActiveForEdgeCalc = true;
                            arrow.isSideItemPresent = false;
                            arrow.isVisibleInCanvas = true;
                        }
                    });
                    shape.drawingEdges.forEach((drawingEdge) => {
                        const reset = resetsForThisShape.find(
                            (item) => item.sideItem.arrowInfo.drawingEdgeId === drawingEdge.id
                        );
                        if (reset) {
                            drawingEdge.isVisibleInCanvas = true;
                            drawingEdge.sideItem.type = undefined; // TODO EZE-1448
                        }
                    });
                    if (shape.removedElementsHistory?.edgesAndCorners) {
                        shape.removedElementsHistory.edgesAndCorners =
                            shape.removedElementsHistory?.edgesAndCorners.filter(
                                (removedEdge) =>
                                    !resetsForThisShape.some(
                                        (item) => item.sideItem.arrowInfo.drawingEdgeId === removedEdge.drawingEdge.id
                                    )
                            );
                    }
                }
            });
        }

        //update drawingShapes with productMatchingSummaries
        updateDrawingShapes(productMatchingSummaries.drawingShapes.newValueAfterReplacements);

        // dispatch(updateSelectedFaucets(productMatchingSummaries.faucets.newValueAfterReplacements));
    };

    const getAlerts = useCallback(
        async (args: ProductMatchingTargetParameters): Promise<AlertWithMetadata[]> => {
            const summaries = await makeDesignProductMatchingSummary(args);
            if (!summaries) {
                const zipMessage = args?.zipCode
                    ? i18n.t('EQUIVALENT_PRODUCT_MATCHING.ZIP_CODE.ALERT_TEXT', {
                          zipCode: args.zipCode,
                      })
                    : undefined;
                const storeMessage = args?.storeId
                    ? i18n.t('EQUIVALENT_PRODUCT_MATCHING.STORE.ALERT_TEXT', {
                          storeId: args.storeId,
                      })
                    : undefined;
                return [
                    {
                        label: storeMessage ?? zipMessage ?? 'An error has occurred',
                        userMustCancel: true,
                    },
                ];
            }
            return Object.values(summaries ?? emptyObject).reduce(summaryToAlertsWithMetadataReducer, emptyArray);
        },
        [makeDesignProductMatchingSummary]
    );

    return {
        replaceProductsWithEquivalents,
        makeDesignProductMatchingSummary,
        getAlerts,
    };
};
